import Groups from "@mui/icons-material/Groups";
import RestartAlt from "@mui/icons-material/RestartAlt";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { generatePrintoutPdf } from "../../../downloads/downloadDayViewPdf";
import { handleDownload, printPdfBlob } from "../../../downloads/utils";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { SaveAsDefaultDialog } from "../../SaveAsDefault/SaveAsDefaultDialog";
import type { DownloadFormat } from "../../UIToolkit/Clickable/DownloadFormatButton";
import { DownloadFormatButton } from "../../UIToolkit/Clickable/DownloadFormatButton";
import { DateRangeSelector } from "../../UIToolkit/molecules/DateRangeSelector";
import { SpreadsheetPreview } from "../../UIToolkit/Previews/SpreadsheetPreview";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { SCHEDULE_EXPORT_TEST_ID } from "./consts";
import { dayColumnWidths, reindexForHireDate } from "./dayColumnWidths";
import { MonthlyExportSettings } from "./MonthlyExportSettings";
import { normalizeMonthlySettings } from "./monthlySettings";
import { ScheduleExportTabLayout } from "./ScheduleExportTabLayout";
import type { MonthlyExportSettingsValue, MonthlySortEntry, PersistedMonthlyExportSettings } from "./types";
import { useHydratedSettings } from "./useHydratedSettings";
import { useMonthlyScheduleExportPreview } from "./useMonthlyScheduleExportPreview";
import type { useScheduleExportAnalytics } from "./useScheduleExportAnalytics";
import { useScheduleExportSettings } from "./useScheduleExportSettings";
import { useTrackPreviewLifecycle } from "./useTrackPreviewLifecycle";

const FILE_DATE_FORMAT = "yyyy-MM-dd";

/** Matches the settings save, so one drag produces one PUT and one refetch. */
const COLUMN_WIDTH_REFETCH_DEBOUNCE_MS = 600;

/**
 * "location:asc,role:desc" — `trackSettingChanged`'s `value` param is `string | boolean | number`
 * and every other setting is already one of those; `sortEntries` is the one multi-value setting, so
 * it is serialized to a readable string here rather than widening the tracker's signature for every
 * other caller. No existing serialization precedent for a multi-value setting was found elsewhere
 * in this codebase to reuse (checked: every other `trackSettingChanged` call site is a plain
 * primitive) — this format mirrors the `column:direction` shorthand already used for a sort query
 * param in `frontend/src/pages/Dev/SystemDataGridFixture.tsx`.
 */
const serializeSortEntriesForAnalytics = (sortEntries: MonthlySortEntry[]): string =>
	sortEntries.map((entry) => `${entry.column}:${entry.direction}`).join(",");

export type MonthlyExportTabProps = {
	/** False in the minimized card, which has no room for a spreadsheet. */
	showPreview: boolean;
	analytics: ReturnType<typeof useScheduleExportAnalytics>;
	onDownloaded: () => void;
};

export const MonthlyExportTab: FC<MonthlyExportTabProps> = ({ showPreview, analytics, onDownloaded }) => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();

	const [range, setRange] = useState<{ start: DateTime; end: DateTime }>(() => {
		const now = DateTime.now().setZone(chosenDepartment.timezone);
		return { start: now.startOf("month"), end: now.endOf("month").startOf("day") };
	});
	const { monthly, saveMonthly, canSaveAsDepartmentDefault, saveMonthlyAsDepartmentDefault, resetMonthlyToDepartmentDefault } = useScheduleExportSettings();
	// Local for responsiveness, seeded from the stored document; the save is debounced behind it. Keeping
	// the panel on local state is what stops a settings refetch from fighting a control mid-interaction.
	// Adopts the stored document once the settings query resolves — see `useHydratedSettings`.
	const [storedSettings, setMonthlySettings] = useHydratedSettings<PersistedMonthlyExportSettings>(monthly);
	// Normalized on READ rather than with a mount-time PUT, so hydration stays side-effect free: the
	// preview request and the next real edit both see the corrected document, and that edit is what
	// persists it. See `normalizeMonthlySettings` for what the pair breaks.
	const monthlySettings = useMemo(() => normalizeMonthlySettings(storedSettings), [storedSettings]);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isPrinting, setIsPrinting] = useState(false);
	const [isSaveAsDefaultDialogOpen, setIsSaveAsDefaultDialogOpen] = useState(false);

	// `columnWidths` is measured from the preview rather than chosen in the panel, so it lives in the
	// same document but never routes through `handleSettingChange` — that separation is what keeps a
	// column drag out of `trackSettingChanged`.
	const columnWidths = monthlySettings.columnWidths;

	// Read through a ref so the two callbacks below keep a stable identity for the tab's lifetime.
	// Fortune Sheet lists `onChange` in its own effect's deps, so a new `setColumnWidths` identity
	// re-invokes it with unchanged data — and the save must not sit inside the state updater, which
	// React is free not to run once the tab starts unmounting.
	const settingsRef = useRef(monthlySettings);
	settingsRef.current = monthlySettings;

	const setColumnWidths = useCallback(
		(widths: Record<string, number>) => {
			const next = { ...settingsRef.current, columnWidths: widths };
			setMonthlySettings(next);
			saveMonthly(next);
		},
		[saveMonthly, setMonthlySettings],
	);

	const dayCount = Math.round(range.end.diff(range.start, "days").days) + 1;

	const setDayColumnWidth = useCallback(
		(widthPx: number) => {
			const current = settingsRef.current;
			// Merged over the existing overrides rather than replacing them, so a width dragged onto a
			// leading column survives setting every day column at once.
			setColumnWidths({ ...current.columnWidths, ...dayColumnWidths(widthPx, current.showHireDate, dayCount) });
			analytics.trackDayColumnWidthSet(widthPx);
		},
		[analytics, dayCount, setColumnWidths],
	);

	const { buffer, sheets, isLoading, errorMessage, isMissingScheduleHistory, refetch } = useMonthlyScheduleExportPreview({
		departmentId: chosenDepartment.department_id,
		startDate: range.start,
		endDate: range.end,
		...monthlySettings,
		// Fetch in both size states: it is one request, the cache makes maximizing instant, and the
		// download needs the bytes whether or not the preview is on screen.
		enabled: true,
	});

	// Widths are absent from the query key on purpose (see `useMonthlyScheduleExportPreview`), so the
	// refetch that makes the downloaded bytes match the widths on screen has to be asked for. Once, when
	// the drag settles — not per pointer move.
	const settledColumnWidths = useDebouncedValue(columnWidths, COLUMN_WIDTH_REFETCH_DEBOUNCE_MS);
	const refetchedWidths = useRef(settledColumnWidths);
	useEffect(() => {
		// Seeded with the first value, so mounting does not refetch what was just fetched.
		if (refetchedWidths.current === settledColumnWidths) return;
		refetchedWidths.current = settledColumnWidths;
		void refetch();
	}, [settledColumnWidths, refetch]);

	useTrackPreviewLifecycle(isLoading, Boolean(errorMessage), analytics);

	// Monthly preview failures were entirely untracked; the weekly path had this from the start.
	const reportedPreviewError = useRef<string | null>(null);
	useEffect(() => {
		if (!errorMessage) {
			reportedPreviewError.current = null;
			return;
		}
		if (reportedPreviewError.current === errorMessage) return;
		reportedPreviewError.current = errorMessage;
		analytics.trackPreviewError(errorMessage, { is_missing_schedule_history: isMissingScheduleHistory });
	}, [errorMessage, isMissingScheduleHistory, analytics]);

	const handleSettingChange = useCallback(
		<K extends keyof MonthlyExportSettingsValue>(key: K, next: MonthlyExportSettingsValue[K]) => {
			const updated = { ...settingsRef.current, [key]: next };
			// Gaps are per shift-time window, so switching to flat mode has to clear the flag as well as
			// disable the control — otherwise a stored `true` would keep travelling with the document.
			if (key === "shiftTimeSeparation" && next === "none") updated.printGaps = false;
			// Column widths are keyed by ABSOLUTE column index, so showing or hiding the hire-date column
			// shifts every day column by one. Without this the stored widths stay put and each one starts
			// describing its neighbour instead.
			if (key === "showHireDate") updated.columnWidths = reindexForHireDate(updated.columnWidths, Boolean(next));
			setMonthlySettings(updated);
			saveMonthly(updated);
			// `next`'s type is `MonthlyExportSettingsValue[K]` for a generic `K` — checking
			// `key === "sortEntries"` at runtime does not narrow it for TS, so both branches need an
			// explicit cast to the tracker's `string | boolean | number`. Every key OTHER than
			// `sortEntries` really is one of those three; only `sortEntries` (`MonthlySortEntry[]`) is
			// not, and it never reaches the `else` branch. `sortBy` itself is a plain frozen string, so
			// it needs no special-casing at all.
			const trackedValue = key === "sortEntries" ? serializeSortEntriesForAnalytics(next as MonthlySortEntry[]) : (next as string | boolean | number);
			analytics.trackSettingChanged(key, trackedValue);
		},
		[analytics, saveMonthly, setMonthlySettings],
	);

	const handleRangeChange = useCallback(
		(edge: "start" | "end") => (date: DateTime) => {
			setRange((previous) =>
				edge === "start"
					? { start: date, end: previous.end < date ? date : previous.end }
					: // A typed date bypasses the picker's `minDate`, so without this the request goes out with
						// start after end and the panel shows a generic failure.
						{ start: previous.start > date ? date : previous.start, end: date },
			);
			analytics.trackRangeChanged(edge, { selected_date: date.toFormat(FILE_DATE_FORMAT) });
		},
		[analytics],
	);

	// The download writes the preview's own cached bytes, so whatever stopped the preview stops the
	// download too — and the tooltip says so with the same wording the panel is already showing.
	// Bytes on hand win over a stale error: a background refetch that failed must not disable a download
	// whose preview is still on screen and still valid.
	const hasUsableBytes = Boolean(buffer) && sheets.length > 0;
	// Widths are absent from the query key, so a drag leaves the cached bytes one width behind until the
	// debounced refetch below catches up. The preview already shows the new width (Fortune Sheet holds it
	// locally), so without this the download would hand over a workbook that does not match the screen.
	const widthsPending = columnWidths !== settledColumnWidths;
	const downloadDisabledReason =
		isLoading || widthsPending ? t("preparing.schedule") : hasUsableBytes ? undefined : (errorMessage ?? t("no.data.available.to.export"));

	// Available to every head nurse, matching the Profile weekly export — only SAVING a default is
	// support-gated. The restored document arrives through the settings cache, so the panel follows it
	// without any local reset here.
	const handleResetToDepartmentDefault = useCallback(async () => {
		try {
			await resetMonthlyToDepartmentDefault();
			analytics.trackResetToDepartmentDefault();
		} catch (error) {
			analytics.trackDepartmentDefaultError("reset", error);
			showErrorSnackbar(t("download.settings.reset.failed"), {}, error);
		}
	}, [resetMonthlyToDepartmentDefault, analytics, showErrorSnackbar, t]);

	const handleConfirmSaveAsDepartmentDefault = useCallback(async () => {
		// Checked here and not only where the button and dialog are rendered: `saveMonthlyAsDepartmentDefault`
		// no-ops for a non-support user, so without this the success event below would report a department
		// default that was never written.
		if (!canSaveAsDepartmentDefault) return;
		setIsSaveAsDefaultDialogOpen(false);
		try {
			// From the ref, not the render-time value: the dialog sits between the click and the confirm, so
			// a debounced edit can land in between and the default must be what the panel now shows.
			await saveMonthlyAsDepartmentDefault(settingsRef.current);
			analytics.trackSavedAsDepartmentDefault();
		} catch (error) {
			analytics.trackDepartmentDefaultError("save", error);
			showErrorSnackbar(t("download.settings.save.as.default.failed"), {}, error);
		}
	}, [canSaveAsDepartmentDefault, saveMonthlyAsDepartmentDefault, analytics, showErrorSnackbar, t]);

	// Shared by download and print — both hand the SAME generated PDF to a different next step, and the
	// backend has no PDF path at all (this renders from the sheets already on screen, so it costs no
	// second request).
	const buildMonthlyPdf = useCallback(
		() =>
			generatePrintoutPdf(
				sheets,
				settingsRef.current.orientation,
				settingsRef.current.fitToPage,
				settingsRef.current.fitToWidth,
				settingsRef.current.fitToHeight,
			),
		[sheets],
	);

	const handleDownloadClick = useCallback(
		(format: DownloadFormat) => {
			if (!buffer) return;
			setIsDownloading(true);
			analytics.trackDownloadStart(format);
			const baseName = `monthly_schedule_${range.start.toFormat(FILE_DATE_FORMAT)}_to_${range.end.toFormat(FILE_DATE_FORMAT)}`;
			try {
				if (format === "pdf") {
					handleDownload(buildMonthlyPdf(), `${baseName}.pdf`, "application/pdf");
				} else {
					handleDownload(buffer, `${baseName}.xlsx`);
				}
				analytics.trackDownloadSuccess(format);
				onDownloaded();
			} catch (error) {
				analytics.trackDownloadError(format, error);
				showErrorSnackbar(t(format === "pdf" ? "download.pdf.failed" : "download.excel.failed"), {}, error);
			} finally {
				setIsDownloading(false);
			}
		},
		[buffer, range, analytics, onDownloaded, showErrorSnackbar, t, buildMonthlyPdf],
	);

	// PDF-only: printing hands the browser the same page-fit layout the PDF download builds, which makes
	// no sense for the raw Excel grid.
	const handlePrintClick = useCallback(async () => {
		if (!buffer) return;
		setIsPrinting(true);
		analytics.trackPrintStart();
		try {
			// Awaited so a failure that only surfaces once the iframe loads (a missing contentWindow, or
			// `print()` itself throwing) is actually caught here instead of being reported as a success.
			await printPdfBlob(buildMonthlyPdf());
			analytics.trackPrintSuccess();
			onDownloaded();
		} catch (error) {
			analytics.trackPrintError(error);
			showErrorSnackbar(t("download.print.failed"), {}, error);
		} finally {
			setIsPrinting(false);
		}
	}, [buffer, analytics, onDownloaded, showErrorSnackbar, t, buildMonthlyPdf]);

	return (
		<>
			<ScheduleExportTabLayout
				showPreview={showPreview}
				settings={
					<>
						<DateRangeSelector
							startDate={range.start}
							endDate={range.end}
							onStartDateChange={handleRangeChange("start")}
							onEndDateChange={handleRangeChange("end")}
							padding={0}
							removeBackground
							removeBorder
							startDateTestId={`${SCHEDULE_EXPORT_TEST_ID}-monthly-start-date`}
							endDateTestId={`${SCHEDULE_EXPORT_TEST_ID}-monthly-end-date`}
						/>
						<MonthlyExportSettings value={monthlySettings} onChange={handleSettingChange} onDayColumnWidth={setDayColumnWidth} />
					</>
				}
				preview={
					<SpreadsheetPreview
						dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-monthly-preview`}
						sheets={sheets}
						isLoading={isLoading}
						columnWidths={columnWidths}
						onColumnWidthsChange={setColumnWidths}
						// The backend's no-schedule-history message explains itself and no retry will change
						// it; any other failure is worth another attempt.
						emptyLabel={errorMessage}
						action={
							errorMessage && !isMissingScheduleHistory ? (
								<SystemButton data-testid={`${SCHEDULE_EXPORT_TEST_ID}-monthly-retry`} buttonVariant="secondary" size="small" onClick={() => void refetch()}>
									{t("retry")}
								</SystemButton>
							) : undefined
						}
						sx={{ flex: 1 }}
					/>
				}
				secondaryActions={
					<>
						<SystemButton
							data-testid={`${SCHEDULE_EXPORT_TEST_ID}-monthly-reset-settings`}
							buttonVariant="secondary"
							tooltip={showPreview ? "" : t("reset.to.default")}
							onClick={() => void handleResetToDepartmentDefault()}
							leadingIcon={<RestartAlt />}
						>
							{showPreview ? t("reset.to.default") : ""}
						</SystemButton>
						{canSaveAsDepartmentDefault && (
							<SystemButton
								data-testid={`${SCHEDULE_EXPORT_TEST_ID}-monthly-save-as-department-default`}
								buttonVariant="borderless"
								tooltip={showPreview ? "" : t("save.as.department.default")}
								onClick={() => setIsSaveAsDefaultDialogOpen(true)}
								leadingIcon={<Groups />}
							>
								{showPreview ? t("save.as.department.default") : ""}
							</SystemButton>
						)}
					</>
				}
				actions={
					<DownloadFormatButton
						dataTestIdPrefix={`${SCHEDULE_EXPORT_TEST_ID}-monthly`}
						formats={["xlsx", "pdf"]}
						onDownload={handleDownloadClick}
						isDownloading={isDownloading}
						disabled={Boolean(downloadDisabledReason)}
						disabledReason={downloadDisabledReason}
						onPrint={() => void handlePrintClick()}
						isPrinting={isPrinting}
					/>
				}
			/>
			{canSaveAsDepartmentDefault && (
				<SaveAsDefaultDialog
					open={isSaveAsDefaultDialogOpen}
					onClose={() => setIsSaveAsDefaultDialogOpen(false)}
					onConfirm={handleConfirmSaveAsDepartmentDefault}
					dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-monthly-save-as-default-dialog`}
				/>
			)}
		</>
	);
};
